import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/LegalLayout";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Headphones, ShieldAlert, RotateCcw, Copyright, Scale,
  ShieldCheck, BarChart3, Mailbox, PackageOpen, ChevronRight, ArrowRight
} from "lucide-react";

const links = [
  { to: "/disputes", title: "Disputes & Reports", desc: "Raise an issue with an order, seller or listing.", icon: ShieldAlert },
  { to: "/return-refund-policy", title: "Return & Refund Policy", desc: "Your rights, timelines and how refunds work.", icon: RotateCcw },
  { to: "/return-policy", title: "Return Policy", desc: "Step-by-step guide to returning an item.", icon: PackageOpen },
  { to: "/report-ip-violation", title: "Report an IP Violation", desc: "Rights holders can report infringing listings.", icon: Copyright },
  { to: "/dsa-osa", title: "DSA / OSA Information", desc: "Our EU & UK online-safety obligations.", icon: Scale },
  { to: "/integrity-compliance", title: "Integrity & Compliance", desc: "How we keep the marketplace safe and fair.", icon: ShieldCheck },
  { to: "/transparency", title: "Transparency Center", desc: "How we moderate and enforce our policies.", icon: BarChart3 },
  { to: "/offline-complaints", title: "Offline Complaints Portal", desc: "File a complaint by post or without an account.", icon: Mailbox },
];

const faqs = [
  { q: "How do I track my order?", a: "Go to Account → Orders to see live status and delivery updates for every purchase." },
  { q: "How long do refunds take?", a: "Approved refunds are issued to your original payment method within 14 days of the seller receiving your return." },
  { q: "How do I contact a seller?", a: "Open the product or order page and use the seller contact option, or raise a case via Disputes & Reports." },
  { q: "Is my payment secure?", a: "Yes. Payments are processed by our certified partners over encrypted connections. We never store your full card details." },
  { q: "What currencies do you support?", a: "All prices and payments on Xurray Mart are in euros (EUR)." },
];

export default function CustomerService() {
  useEffect(() => { document.title = "Customer Service — Xurray"; }, []);
  return (
    <Layout hideAd>
      <div data-testid="customer-service-page">
        <PageHero
          title="How can we help?"
          eyebrow="Customer Service"
          icon={Headphones}
          intro="Browse our help topics, review a policy, or reach out directly — our team responds as quickly as possible."
        />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-2 gap-4 mb-16">
            {links.map(({ to, title, desc, icon: Icon }, i) => (
              <Link
                key={to}
                to={to}
                data-testid={`cs-link-${to.replace(/\//g, "")}`}
                style={{ animationDelay: `${i * 40}ms` }}
                className="group animate-fade-up flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg"
              >
                <span className="rounded-xl bg-primary/10 p-3 text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground"><Icon className="w-5 h-5" /></span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1 font-semibold">{title}<ChevronRight className="w-4 h-4 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 text-primary" /></span>
                  <span className="block text-sm text-muted-foreground mt-0.5">{desc}</span>
                </span>
              </Link>
            ))}
          </div>

          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-12 items-start">
            <div>
              <h2 className="font-display font-bold text-2xl mb-4">Frequently asked questions</h2>
              <Accordion type="single" collapsible className="rounded-2xl border border-border/60 bg-card px-5" data-testid="cs-faq">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="last:border-b-0">
                    <AccordionTrigger className="font-medium">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div className="mt-8 lg:mt-0 rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-6 lg:sticky lg:top-24">
              <Headphones className="w-8 h-8 mb-3 opacity-90" />
              <h3 className="font-display font-bold text-lg">Still need help?</h3>
              <p className="text-sm text-primary-foreground/80 mt-1 mb-5">Send us a message and our support team will get back to you.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-white text-primary px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="cs-contact-cta">
                Contact us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
}
